import { AddressKind } from '@ail/customers-api'
import {
  ContractState,
  OrderInternalFormFieldViewModel,
  OrderInternalViewModel,
  OrderSuppliersFormFieldViewModel,
  OrderSuppliersViewModel,
  OrderType,
  WorkflowOrderKind,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'
import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RenderCellProps } from 'react-data-grid'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { UserContext } from '../../auth/AuthContext'
import { useAuth } from '../../auth/UseAuth'
import { Arr } from '../../helpers/Arr'
import { getEntityKind, getValidDate, printDate } from '../../helpers/Utils'
import { useSettings } from '../../hooks/useSettings'
import { useGetAllCustomersQuery, useGetAllUnitsQuery, useGetTenantsQuery } from '../../redux/api/customersApi'
import {
  useGetOrderInternalQuery,
  useGetOrdersFormsQuery,
  useGetOrdersInternalQuery,
  useGetOrdersSuppliersQuery,
  useGetOrderSuppliersQuery,
  useGetWorkflowConfigsQuery,
  useUpdateOrderInternalMutation,
  useUpdateOrderSuppliersMutation,
} from '../../redux/api/ordersApi'
import { emptyGuid } from '../../types/constants'
import { EntityKind, GridColumnKeys, PermissionsEnum } from '../../types/enums'
import { IconCell } from '../common/Grid/Cells/IconCell/IconCell'
import { LoadingCell } from '../common/Grid/Cells/LoadingCell/LoadingCell'
import { StatusCell } from '../common/Grid/Cells/StatusCell/StatusCell'
import { TextCell } from '../common/Grid/Cells/TextCell/TextCell'
import { Grid, GridColumn } from '../common/Grid/Grid'
import { PopupType } from '../common/Popup/Popup'
import { usePopup } from '../common/Popup/PopupContext'
import { BooleanModal } from './BooleanModal/BooleanModal'
import { DetailsModal } from './DetailsModal/DetailsModal'
import { DocumentsModal } from './DocumentsModal/DocumentsModal'
import { ManagerModal } from './ManagerModal/ManagerModal'
import './Orders.scss'
import { RequestDetailsModal } from './RequestDetailsModal/RequestDetailsModal'
import { SchedulerModal } from './SchedulerModal/SchedulerModal'
import { SelectAndEditModal } from './SelectAndEditModal/SelectAndEditModal'
import { StatusModal } from './StatusModal/StatusModal'
import { TextModal } from './TextModal/TextModal'
import { useSearchWorkflowsQuery } from '../../redux/api/workflowsApi'
import { workflowHub } from '../../redux/hubs/workflowsHub'
import { WorkflowStatus } from '@ail/workflows-api'
import { Loader } from '../common/Loader/Loader'

export interface Field extends OrderInternalFormFieldViewModel, OrderSuppliersFormFieldViewModel {}

export type Order = (OrderInternalViewModel | OrderSuppliersViewModel) & {
  _fields: Record<string, Field | undefined>
  tenantId?: string
  contractState?: ContractState | undefined
  contractStartDate?: string | undefined
  contractEndDate?: string | undefined
  currentCompanyName?: string
  currentCompanyId?: string
  currentCostCenterName?: string
  currentCostCenterId?: string
  invoiceAddress?: string
  relatedOrderId?: string
}

const DATE_FIELDS = ['Faktura_Datum', 'BKW_start_date', 'earliest_delivery', 'confirmed_delivery']

const OrderTypeIcons: Record<OrderType, string> = {
  [OrderType.Generic]: 'icon-requests',
  [OrderType.VehicleOrder]: 'icon-order',
  [OrderType.FuelCard]: 'icon-fuelcard',
  [OrderType.ContractChange]: 'icon-change-driver',
  [OrderType.DocumentUpload]: 'icon-doc-folder',
  [OrderType.FinancialChange]: 'icon-doc-invoice',
}

export function Orders(props: { kind: WorkflowOrderKind }) {
  const [statusModalOrder, setStatusModalOrder] = useState<Order>()
  const [workflowStatuses, setWorkflowStatuses] = useState<Partial<Record<string, WorkflowStatus>>>({})
  const [documentsModalData, setDocumentsModalData] = useState<{ order: Order; field: Field }>()
  const [managerModalData, setManagerModalData] = useState<{ order: Order; field: Field }>()
  const [selectAndEditModalData, setSelectAndEditModalData] = useState<{ order: Order; field: Field }>()
  const [booleanModalData, setBooleanModalData] = useState<{ order: Order; field: Field }>()
  const [textModalData, setTextModalData] = useState<{ order: Order; field: Field }>()
  const [schedulerModalData, setSchedulerModalData] = useState<{ order: Order; field: Field }>()
  const timestamp = useRef(new Date().getTime())

  const { user } = useAuth()
  const params = useParams()
  const navigate = useNavigate()
  const [t, i18n] = useTranslation()

  const [tenantId, updateTenantId, tenantIdLoading] = useSettings(
    props.kind === WorkflowOrderKind.Outgoing ? 'iLinkOutgoingTenant' : 'iLinkIncomingTenant'
  )
  const [orderTypes, updateOrderTypes] = useSettings('iLinkOrderTypes')

  const loadSuppliers = user?.isInternalUser ? !!tenantId && tenantId !== emptyGuid : true
  const suppliersArgs = loadSuppliers
    ? { tenantId: user?.isInternalUser ? tenantId : undefined, orderTypes: user?.isInternalUser ? orderTypes : [] }
    : skipToken
  const loadInternal = user?.isInternalUser && tenantId === emptyGuid && orderTypes.length
  const internalArgs = loadInternal ? { orderTypes } : skipToken

  const tenants = useGetTenantsQuery(undefined, { skip: !user?.isInternalUser })
  const allCustomers = useGetAllCustomersQuery()
  const allUnits = useGetAllUnitsQuery()

  const forms = useGetOrdersFormsQuery(undefined, { skip: !user?.isInternalUser })
  const workflowConfigs = useGetWorkflowConfigsQuery(undefined, { skip: !user?.isInternalUser })
  const ordersInternal = useGetOrdersInternalQuery(internalArgs)
  const ordersSuppliers = useGetOrdersSuppliersQuery(suppliersArgs)

  const internalDetailsArgs = params.orderId && user?.isInternalUser ? { id: params.orderId } : skipToken
  const supplierDetailsArgs = params.orderId ? { id: params.orderId } : skipToken
  const internalDetail = useGetOrderInternalQuery(internalDetailsArgs)
  const supplierDetail = useGetOrderSuppliersQuery(supplierDetailsArgs)
  const workflows = useSearchWorkflowsQuery({}, { skip: !user?.isInternalUser })

  useEffect(() => {
    if (!workflows.currentData) return
    setWorkflowStatuses(
      Arr.toObject(
        workflows.currentData.workflows,
        (w) => w.id,
        (w) => w.status
      )
    )
  }, [workflows.currentData])

  const currentOrders = loadSuppliers
    ? ordersSuppliers.currentData
    : loadInternal
      ? ordersInternal.currentData
      : undefined

  const [, updateInternalResult] = useUpdateOrderInternalMutation({
    fixedCacheKey: 'UpdateInternal',
  })
  const [, updateSuppliersResult] = useUpdateOrderSuppliersMutation({
    fixedCacheKey: 'UpdateSuppliers',
  })
  const popup = usePopup()

  const isFetching = tenants.isFetching || ordersInternal.isFetching || ordersSuppliers.isFetching
  const isUpdating = updateInternalResult.isLoading || updateSuppliersResult.isLoading
  const loading = isFetching || isUpdating

  const customers = useMemo(() => {
    return Arr.toObject(
      allCustomers.data ?? [],
      (c) => c.customer!.id.toLowerCase(),
      (c) => c.customer!
    )
  }, [allCustomers.data])

  const units = useMemo(() => {
    return Arr.toObject(
      allUnits.data ?? [],
      (c) => c.unit!.id.toLowerCase(),
      (c) => c.unit!
    )
  }, [allUnits.data])

  const workflowTenants = useMemo(() => {
    return tenants.currentData?.filter((t) => {
      const isPro = t.tags.some((tag) => tag.toLowerCase() === 'interfleetpro')
      const hasOrders = workflowConfigs.currentData?.some((w) =>
        w.orders.some((o) => o.tenantId && o.tenantId === t.id)
      )
      return isPro && hasOrders
    })
  }, [tenants.currentData, workflowConfigs.currentData])

  const actualWorkflows = useMemo(() => {
    const workflowIds = forms.currentData
      ?.map((f) => (props.kind === WorkflowOrderKind.Outgoing ? f.approvedOrderWorkflowId : f.returnOrderWorkflowId))
      .filter((i) => !!i)
    return workflowConfigs.currentData?.filter((w) => workflowIds?.includes(w.id)) ?? []
  }, [forms.currentData, props.kind, workflowConfigs.currentData])

  useEffect(() => {
    if (user?.isInternalUser && !tenantIdLoading && workflowTenants?.length) {
      const tenantValid = tenantId === emptyGuid || workflowTenants.some((t) => t.id === tenantId)
      if (!tenantValid) {
        updateTenantId(emptyGuid)
      }
    }
  }, [tenantId, tenantIdLoading, updateTenantId, user?.isInternalUser, workflowTenants])

  useEffect(() => {
    const updatedTimestamp = Math.max(
      updateInternalResult.fulfilledTimeStamp ?? 0,
      updateSuppliersResult.startedTimeStamp ?? 0
    )
    if (updatedTimestamp > timestamp.current) {
      timestamp.current = new Date().getTime()
      popup?.pushPopup('Data has been updated', PopupType.Success)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateInternalResult, updateSuppliersResult])

  useEffect(() => {
    if (!user?.isInternalUser) {
      return
    }

    let cancel: () => void = () => {}
    const cancelled = new Promise<void>((resolve) => {
      cancel = resolve
    })

    workflowHub()
      .receiveWorkflowUpdated((w) => {
        setWorkflowStatuses((current) => {
          return { ...current, [w.id]: w.status }
        })
      })
      .receiveWorkflowCreated((w) => {
        setWorkflowStatuses((current) => {
          return { ...current, [w.id]: w.status }
        })
      })
      .start(cancelled)

    return () => cancel()
  }, [user?.isInternalUser])

  const formatDate = (value?: string) => {
    if (value && /(\d{2})\.(\d{2})\.(\d{4})/.test(value) === false) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('fr-CH')
      }
    }
    return value ?? '-'
  }

  const isBooleanString = useCallback((field?: Field) => {
    return !field ? 'undefined' : isFalsyValue(field.value) ? 'false' : 'true'
  }, [])

  const checkDocumentAvailability = useCallback((field?: Field) => {
    if (!field) return 'noDocuments'
    const requiredDocs = field.documents.every((d) => d.documentId || !d.required)
    const anyDoc = field.documents.some((d) => d.documentId)

    return requiredDocs && anyDoc ? 'allDocuments' : !requiredDocs && anyDoc ? 'partialDocuments' : 'noDocuments'
  }, [])

  const checkDocumentWorkflows = useCallback(
    (field?: Field) => {
      if (!field) return ''
      const states = new Set<string>()
      for (const doc of field.documents) {
        if (!doc.workflowId) {
          if (doc.workflowKind && doc.documentId) states.add('workflowCanStart')
          continue
        }
        const status = workflowStatuses[doc.workflowId]
        // The workflow exists, but its status could not be fetched (workflows api unresponsive).
        if (!status) states.add('workflowUnavailable')
        else if (status === WorkflowStatus.Complete) states.add('workflowComplete')
        else if (status === WorkflowStatus.Terminated) states.add('workflowTerminated')
        else states.add('workflowRunning')
      }
      return [...states].join(' ')
    },
    [workflowStatuses]
  )

  const isFalsyValue = (value?: string) => {
    return !value || ['false', 'n', 'none'].includes(value.toLowerCase())
  }

  const dynamicCell = useCallback(
    (data: RenderCellProps<Order, Record<string, string[] | undefined>>) => {
      const field = data.row._fields[data.column.key]
      const readonly =
        !field ||
        (data.row.tenantId && user?.isInternalUser && !user.isAdmin) ||
        field.readonly ||
        field.name === 'AssignedBy' ||
        field.name === 'AssignedAt' ||
        (data.row.form.status === WorkflowOrderStepStatus.Done && !user?.isAdmin)
      if (!field) {
        return <></>
      } else if (field.name === 'AssignedTo') {
        return (
          <TextCell disabled={readonly} onClick={() => setManagerModalData({ order: data.row, field })}>
            {field.value || '-'}
          </TextCell>
        )
      } else if (field.type === 'selectAndEdit' || field.externalDataSourceName) {
        return (
          <TextCell disabled={readonly} onClick={() => setSelectAndEditModalData({ order: data.row, field })}>
            {field.value || '-'}
          </TextCell>
        )
      } else if (field.type === 'upload') {
        let anyUploaded = false
        let requiredUploaded = true
        let hasUnstartedWkf = false
        let hasUncompleteWkf = false
        let hasTerminatedWkf = false
        let hasUnknownWkf = false
        for (const doc of field.documents) {
          if (doc.documentId) anyUploaded = true
          if (doc.required && !doc.documentId) requiredUploaded = false
          if (doc.workflowKind && !doc.workflowId && doc.documentId) hasUnstartedWkf = true
          if (!doc.workflowId) continue
          const status = workflowStatuses[doc.workflowId]
          // The workflow exists, but its status could not be fetched (workflows api unresponsive).
          if (!status) hasUnknownWkf = true
          else if (status === WorkflowStatus.Terminated) hasTerminatedWkf = true
          else if (status !== WorkflowStatus.Complete) hasUncompleteWkf = true
        }
        const extraIcon =
          hasUnstartedWkf || hasUncompleteWkf || hasTerminatedWkf || hasUnknownWkf ? 'canStart' : undefined
        const extraIconColor = hasTerminatedWkf
          ? '#ff0000'
          : hasUnstartedWkf
            ? '#ff8800'
            : hasUncompleteWkf
              ? '#30d5c8'
              : 'grey'
        return (
          <IconCell
            icon={field.icon ?? 'icon-doc'}
            color={requiredUploaded && anyUploaded ? '#39b54a' : !requiredUploaded && anyUploaded ? '#f80' : 'grey'}
            onClick={() => {
              setDocumentsModalData({ order: data.row, field })
            }}
            extraIcon={extraIcon ? [extraIcon, extraIconColor] : undefined}
          />
        )
      } else if (field.type === 'date' || DATE_FIELDS.includes(field.name)) {
        return (
          <TextCell disabled={readonly} onClick={() => setSchedulerModalData({ order: data.row, field })}>
            {formatDate(field.value)}
          </TextCell>
        )
      } else if (field.name.includes('Interior') || field.name === 'Roofrack') {
        return (
          <TextCell
            style={{ color: isFalsyValue(field.value) ? '#80808033' : '#000' }}
            disabled={readonly}
            onClick={() => setTextModalData({ order: data.row, field })}
          >
            {field.value || '-'}
          </TextCell>
        )
      } else if (field.type === 'boolean') {
        return (
          <IconCell
            disabled={readonly}
            icon={isFalsyValue(field.value) ? 'icon-close2' : 'icon-check'}
            color={isFalsyValue(field.value) ? '#80808033' : '#39b54a'}
            onClick={() => setBooleanModalData({ order: data.row, field })}
          />
        )
      } else if (field.type === 'text') {
        return (
          <TextCell disabled={readonly} onClick={() => setTextModalData({ order: data.row, field })}>
            {field.value || '-'}
          </TextCell>
        )
      } else {
        return <TextCell disabled={readonly}>{field.value || '-'}</TextCell>
      }
    },
    [user?.isAdmin, user?.isInternalUser, workflowStatuses]
  )

  const contractStateCell = (data: RenderCellProps<Order, Record<string, string[] | undefined>>) => {
    const tooltip = `${data.row.contractState} contract (${printDate(data.row.contractStartDate)} - ${printDate(
      data.row.contractEndDate
    )})`
    switch (data.row.contractState) {
      case 'Running':
      case 'Extended':
        return <IconCell icon="icon-info" color="#60c36e" tooltip={tooltip} />
      case 'Initialized':
        return <IconCell icon="icon-info" color="#30d5c8" tooltip={`Initialized contract`} />
      case 'Terminated':
      case 'Closed':
        return <IconCell icon="icon-warning" color="#ff8800" tooltip={tooltip} />
      case null:
      case undefined:
        return <IconCell icon="icon-info" color="grey" tooltip={`No contract present`} />
    }
    return <>-</>
  }

  const companyCell = useCallback(
    (data: RenderCellProps<Order, Record<string, string[] | undefined>>) => {
      if (data.row.currentCompanyId !== data.row.companyId && allCustomers.isLoading) {
        return <LoadingCell />
      }
      const updated = data.row.companyId.toLowerCase() === data.row.currentCompanyId?.toLowerCase()
      return (
        <TextCell style={{ fontWeight: updated ? 'normal' : 'bold' }}>{data.row.currentCompanyName ?? '-'}</TextCell>
      )
    },
    [allCustomers.isLoading]
  )

  const costCenterCell = useCallback(
    (data: RenderCellProps<Order, Record<string, string[] | undefined>>) => {
      if (data.row.currentCostCenterId !== data.row.costCenterId && allUnits.isLoading) {
        return <LoadingCell />
      }
      const updated = data.row.costCenterId.toLowerCase() === data.row.currentCostCenterId?.toLowerCase()
      return (
        <TextCell style={{ fontWeight: updated ? 'normal' : 'bold' }}>{data.row.currentCostCenterName ?? '-'}</TextCell>
      )
    },
    [allUnits.isLoading]
  )

  const invoiceAddressCell = useCallback(
    (data: RenderCellProps<Order, Record<string, string[] | undefined>>) => {
      if (allCustomers.isLoading) {
        return <LoadingCell />
      }
      return <TextCell>{data.row.invoiceAddress}</TextCell>
    },
    [allCustomers.isLoading]
  )

  const returnLogisticIdCell = useCallback(
    (data: RenderCellProps<Order, Record<string, string[] | undefined>>, user?: UserContext | null) => {
      const canViewIncoming = user?.isInternalUser || user?.permissions?.includes(PermissionsEnum.Incoming)
      return (
        <TextCell>
          {data.row.returnLogisticId}
          {!!data.row.relatedOrderId && data.row.kind === WorkflowOrderKind.Outgoing && canViewIncoming && (
            <IconCell
              icon="icon-info"
              color="black"
              size={15}
              onClick={() => navigate(`/incoming/${data.row.relatedOrderId}/details`)}
            ></IconCell>
          )}
        </TextCell>
      )
    },
    [navigate]
  )

  const setCustomerData = useCallback(
    (order: Order) => {
      order.currentCompanyId = order.companyId.toLowerCase()
      order.currentCostCenterId = order.costCenterId.toLowerCase()
      order.currentCompanyName = order.companyName
      order.currentCostCenterName = order.costCenterName
      if (order.owner) {
        const owners = order.owner?.replace('$', '').split('//')
        const companyId = owners.find((id) => getEntityKind(id) === EntityKind.Customer)
        const unitId = owners.find((id) => getEntityKind(id) === EntityKind.Unit)
        if (unitId && unitId.toLowerCase() !== order.costCenterId) {
          order.currentCostCenterId = unitId.toLowerCase()
          order.currentCostCenterName = units[unitId.toLowerCase()]?.name
        }
        if (companyId && companyId.toLowerCase() !== order.companyId) {
          order.currentCompanyId = companyId.toLowerCase()
          order.currentCompanyName = customers[companyId.toLowerCase()]?.name
        }
      }

      const company = customers[order.currentCompanyId.toLowerCase()]
      const address = company?.primary?.addresses.find((a) => a.kind === AddressKind.Invoice)
      const fullAddress = address ? `${address.street} ${address.postalCode} ${address.city} ${address.country}` : '-'
      order.invoiceAddress = fullAddress.replace('<BR>', ', ')
    },
    [customers, units]
  )

  const columns = useMemo(() => {
    const columnLabels: Record<string, string | undefined> = {}
    const columnTypes: Record<string, string | undefined> = {}

    if (user?.isInternalUser) {
      const fields =
        actualWorkflows.flatMap((c) => {
          return c.orders.flatMap((o) =>
            (!o.tenantId && tenantId === emptyGuid) || o.tenantId === tenantId ? o.form.fields : []
          )
        }) ?? []
      for (const field of fields) {
        if (!field.hidden && (!columnLabels[field.name] || field.tr?.[i18n.language])) {
          columnLabels[field.name] = field.tr?.[i18n.language] ?? field.tr?.['en'] ?? field.name
          columnTypes[field.name] = field.type
        }
      }
    } else {
      for (const order of currentOrders ?? []) {
        if (order.kind !== props.kind) {
          continue
        }
        for (const field of order.form.fields) {
          if (!field.hidden && (!columnLabels[field.name] || field.tr?.[i18n.language])) {
            columnLabels[field.name] = field.tr?.[i18n.language] ?? field.tr?.['en'] ?? field.name
            columnTypes[field.name] = field.type
          }
        }
      }
    }

    const dynamicColumnKeys = Object.keys(columnLabels)

    if (!dynamicColumnKeys.length) {
      return []
    }

    const dynamicColumns = dynamicColumnKeys.map((key) => {
      const type = columnTypes[key]
      return {
        key: `${key}`,
        name: columnLabels[key],
        renderCell: dynamicCell,
        filterItems:
          type === 'boolean'
            ? ['true', 'false', 'undefined']
            : type === 'upload'
              ? [
                  'allDocuments',
                  'partialDocuments',
                  'noDocuments',
                  'workflowCanStart',
                  'workflowRunning',
                  'workflowComplete',
                  'workflowTerminated',
                  'workflowUnavailable',
                ]
              : undefined,
        resizable: true,
        minWidth: getDynamicColumnWidth(key),
        filterBy: (item) => {
          const field = item._fields[key]
          return field?.type === 'date'
            ? formatDate(field?.value)
            : type === 'boolean'
              ? isBooleanString(field)
              : type === 'upload'
                ? `${checkDocumentAvailability(field)} ${checkDocumentWorkflows(field)}`
                : field?.value
        },
        sortBy: (item) => {
          const field = item._fields[key]
          return field?.type === 'date'
            ? getValidDate(field?.value)
            : type === 'boolean'
              ? isBooleanString(field)
              : type === 'upload'
                ? checkDocumentAvailability(field)
                : field?.value
        },
        exportFormat: (item) => {
          const type = item._fields[key]?.type
          const value = item._fields[key]?.value
          return type === 'upload' || type === 'boolean'
            ? value?.split('|')[0]
            : type === 'date' || DATE_FIELDS.includes(key)
              ? formatDate(value)
              : value
        },
      } as GridColumn<Order>
    })
    const result: GridColumn<Order>[] = [
      {
        key: 'orderId',
        name: '',
        remove: !user?.isInternalUser,
        frozen: true,
        minWidth: 50,
        maxWidth: 50,
        renderCell: (data) => (
          <IconCell
            icon={data.row.orderType ? OrderTypeIcons[data.row.orderType] : 'icon-requests'}
            onClick={() => navigate(`/${props.kind.toLowerCase()}/${data.row.orderId}/request`)}
          />
        ),
      },
      {
        key: 'id',
        name: '',
        frozen: true,
        minWidth: 50,
        maxWidth: 50,
        renderCell: (data) => (
          <IconCell icon="icon-show" onClick={() => navigate(`/${props.kind.toLowerCase()}/${data.row.id}/details`)} />
        ),
      },
      {
        key: 'logisticId',
        name: props.kind === WorkflowOrderKind.Outgoing ? t('LOGISTICID')! : t('REPLACEID')!,
        remove: props.kind === WorkflowOrderKind.Incoming && !user?.isInternalUser,
        width: 150,
        filterBy: (item) => item.logisticId,
        sortBy: (item) => item.logisticId,
        exportFormat: (item) => item.logisticId,
      },
      {
        key: 'returnLogisticId',
        name: props.kind === WorkflowOrderKind.Incoming ? t('LOGISTICID')! : t('REPLACEID')!,
        width: 150,
        filterBy: (item) => item.returnLogisticId,
        sortBy: (item) => item.returnLogisticId,
        exportFormat: (item) => item.returnLogisticId,
        renderCell: (data) => returnLogisticIdCell(data, user),
      },
      {
        key: 'companyName',
        name: t('COMPANY')!,
        filterBy: (item) => item.currentCompanyName,
        sortBy: (item) => item.currentCompanyName,
        exportFormat: (item) => item.currentCompanyName,
        width: 250,
        renderCell: companyCell,
      },
      {
        key: 'costCenterName',
        name: t('COST_CENTER')!,
        filterBy: (item) => item.currentCostCenterName,
        sortBy: (item) => item.currentCostCenterName,
        exportFormat: (item) => item.currentCostCenterName,
        renderCell: costCenterCell,
      },
      {
        key: 'invoiceAddress',
        name: t('INVOICE_ADDRESS')!,
        width: 350,
        filterBy: (item) => item.invoiceAddress,
        sortBy: (item) => item.invoiceAddress,
        exportFormat: (item) => item.invoiceAddress,
        renderCell: invoiceAddressCell,
      },
      {
        key: 'contractState',
        name: t('CONTRACT_STATUS')!,
        remove: tenantId !== emptyGuid,
        sortBy: (item) => item.contractState,
        exportFormat: (item) => item.contractState,
        renderCell: contractStateCell,
      },
      {
        key: 'contractReference',
        name: t('REFERENCE')!,
        filterBy: (item) => item.contractReference,
        sortBy: (item) => item.contractReference,
        exportFormat: (item) => item.contractReference,
      },
      ...dynamicColumns,
      {
        key: 'createdAt',
        name: t('CREATED')!,
        renderCell: (data) => printDate(data.row.createdAt, true),
        sortBy: (row) => new Date(row.createdAt),
        exportFormat: (item) => printDate(item.createdAt, true),
      },
      {
        key: 'updatedAt',
        name: t('MODIFIED')!,
        renderCell: (data) => printDate(data.row.updatedAt, true),
        sortBy: (row) => new Date(row.updatedAt),
        exportFormat: (item) => printDate(item.updatedAt, true),
      },
      {
        key: 'status',
        name: t('STATUS')!,
        filterItems: Object.values(WorkflowOrderStepStatus).filter((s) => s !== 'Recreated'),
        width: 130,
        filterBy: (item) => item.form.status,
        sortBy: (item) => item.form.status,
        exportFormat: (item) => t(item.form.status.toUpperCase()),
        renderCell: (data) => (
          <StatusCell status={data.row.form.status} onClick={() => setStatusModalOrder(data.row)}>
            {t(data.row.form.status.toUpperCase())}
          </StatusCell>
        ),
      },
    ]

    return result
  }, [
    companyCell,
    costCenterCell,
    currentOrders,
    dynamicCell,
    i18n.language,
    invoiceAddressCell,
    navigate,
    props.kind,
    returnLogisticIdCell,
    t,
    tenantId,
    user,
    actualWorkflows,
    isBooleanString,
    checkDocumentAvailability,
    checkDocumentWorkflows,
  ])

  const convertOrder = useCallback(
    (src: OrderInternalViewModel | OrderSuppliersViewModel) => {
      const order: Order = { ...src, _fields: {} }
      for (const field of src.form.fields) {
        order._fields[field.name] = field
      }
      setCustomerData(order)
      return order
    },
    [setCustomerData]
  )

  const rows = useMemo(() => {
    const result: Order[] = []
    for (const order of currentOrders ?? []) {
      if (order.kind !== props.kind) {
        continue
      }
      const row = convertOrder(order)
      if (row.kind === WorkflowOrderKind.Outgoing && row.returnLogisticId) {
        const relatedOrder = currentOrders?.find(
          (o) => o.kind === WorkflowOrderKind.Incoming && o.returnLogisticId === row.returnLogisticId
        )
        row.relatedOrderId = relatedOrder?.id
      }
      result.push(row)
    }
    return result
  }, [convertOrder, currentOrders, props.kind])

  function getDynamicColumnWidth(key: string) {
    switch (key) {
      case GridColumnKeys.Brand:
      case GridColumnKeys.Model:
        return 200
      case GridColumnKeys.ChassisNr:
      case GridColumnKeys.VIN:
        return 170
      default:
        return null
    }
  }

  const detailsModalOrder = useMemo(() => {
    const order = supplierDetail.currentData ?? internalDetail.currentData
    if (!order) {
      return undefined
    }
    return convertOrder(order)
  }, [convertOrder, internalDetail.currentData, supplierDetail.currentData])

  const gridIcon = props.kind === WorkflowOrderKind.Outgoing ? 'icon-garageOut' : 'icon-garageIn'

  const gridId = useMemo(() => {
    return user?.isInternalUser ? (tenantId ?? emptyGuid) : user?.tenant
  }, [tenantId, user?.isInternalUser, user?.tenant])

  const reload = () => {
    if (!ordersInternal.isUninitialized) {
      ordersInternal.refetch()
    }
    if (!ordersSuppliers.isUninitialized) {
      ordersSuppliers.refetch()
    }
  }

  const rowClass = useCallback((item: Order) => {
    return `contractStatus ${item.form.status.toLowerCase()}`
  }, [])

  const toggleOrderType = (orderType: OrderType) => {
    const newOrderTypes = orderTypes.includes(orderType)
      ? orderTypes.filter((o) => o !== orderType)
      : [...orderTypes, orderType]

    updateOrderTypes(newOrderTypes.toSorted((a, b) => a.localeCompare(b)))
  }

  return (
    <>
      <Loader blocking={loading}>
        {user?.isInternalUser && (
          <div className="selectViewArea">
            <div className="selectArea">
              <select onChange={(e) => updateTenantId(e.target.value)} value={tenantId}>
                <option key={emptyGuid} value={emptyGuid}>
                  {'Internal'}
                </option>
                {workflowTenants?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <i className="icon-bottom" />
            </div>
            {Object.values(OrderType).map((orderType) => (
              <div
                key={orderType}
                title={orderType}
                className={`group-toggle-row ${orderTypes?.includes(orderType) ? 'active' : ''}`}
                onClick={() => toggleOrderType(orderType)}
              >
                {' '}
                <i className={orderTypes?.includes(orderType) ? 'icon-check active' : 'icon-check inactive'} />
                <i className={OrderTypeIcons[orderType]} />
                <label>{t(orderType.toUpperCase())}</label>
              </div>
            ))}
          </div>
        )}
        <Grid
          id={`${gridId}_${props.kind}`}
          rows={rows}
          columns={columns}
          title={t(props.kind.toUpperCase())}
          icon={gridIcon}
          rowKeyGetter={(row) => row.id}
          onCellDoubleClick={(data) => navigate(`/${props.kind.toLowerCase()}/${data.row.id}/details`)}
          onReload={() => reload()}
          rowClass={rowClass}
        />
      </Loader>
      <StatusModal order={statusModalOrder} onClose={() => setStatusModalOrder(undefined)} />
      {!!documentsModalData && (
        <DocumentsModal data={documentsModalData} onClose={() => setDocumentsModalData(undefined)} />
      )}
      <ManagerModal data={managerModalData} onClose={() => setManagerModalData(undefined)} />
      <BooleanModal data={booleanModalData} onClose={() => setBooleanModalData(undefined)} />
      <TextModal data={textModalData} onClose={() => setTextModalData(undefined)} />
      <SchedulerModal data={schedulerModalData} onClose={() => setSchedulerModalData(undefined)} />
      <DetailsModal
        order={detailsModalOrder}
        workflowStatuses={workflowStatuses}
        onClose={() => navigate(`/${props.kind.toLowerCase()}`)}
      />
      <RequestDetailsModal requestId={params.requestId} onClose={() => navigate(`/${props.kind.toLowerCase()}`)} />
      <SelectAndEditModal data={selectAndEditModalData} onClose={() => setSelectAndEditModalData(undefined)} />
    </>
  )
}
